import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import {
    CONFIRM_DEFAULT_ACTIVATION_TOAST_MESSAGE,
    CONFIRM_DEFAULT_ACTIVATION_TOAST_MESSAGE_REMOVE,
} from "app/pages/activation/components/ConfirmDefaultActivationModal/texts";
import { COMMON_API_ERROR_MESSAGE } from "app/pages/activation/constants/texts";
import { useShortActivationSetListQuery } from "app/pages/activation/queries/data/useShortActivationSetListQuery";
import { useSwitchFavorite } from "app/pages/activation/queries/data/useSwitchFavorite";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ACTIVATION_PROJECT_ROUTE } from "../../routing/constants";
import { useToasts } from "../helpers/useToasts";

export type SetDefaultPageSchema = {
    activationId: string;
    name: string;
    country: number;
    onSuccess?: () => void;
    withRedirect?: boolean;
};

const useDefaultActivation = () => {
    const { params } = useCurrentStateAndParams();

    const { showSuccessToast } = useToasts();

    const track = activationTracking.home;

    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const [selectedActivationData, setSelectedActivationData] = useState<{
        id: string;
        name: string;
        country: number;
    }>({
        id: "",
        name: "",
        country: 0,
    });

    const { data: activationsList, refetch } = useShortActivationSetListQuery();

    const defaultActivation = activationsList?.find((item) => item.isFavorite);
    const hasFavorite = !!defaultActivation;
    const defaultActivationId = defaultActivation?.id;

    const { mutateAsync: switchDefaultActivation } = useSwitchFavorite();

    const redirectToDefaultPage = (activationId: string, country: number) => {
        if (params.key !== activationId) {
            proNavigator.go(
                ACTIVATION_PROJECT_ROUTE,
                {
                    ...params,
                    country,
                    key: activationId,
                },
                { reload: true },
            );
        }
    };

    const setDefaultPage = async ({ activationId, name, country, withRedirect, onSuccess }: SetDefaultPageSchema) => {
        if (defaultActivationId && activationId === defaultActivationId) {
            return;
        }

        if (hasFavorite) {
            setIsConfirmModalOpen(true);
            setSelectedActivationData({ id: activationId, name, country });

            return;
        }

        await switchDefaultActivation(activationId, {
            onSuccess() {
                showSuccessToast(translate(CONFIRM_DEFAULT_ACTIVATION_TOAST_MESSAGE, { name }));
                track.trackDefaultHomepageSet();

                onSuccess?.();

                void refetch();

                if (withRedirect) {
                    redirectToDefaultPage(activationId, country);
                }
            },
        });
    };

    const updateDefaultActivation = async (onSuccess = noop, withRedirect = true) => {
        if (selectedActivationData.id) {
            try {
                setIsLoading(true);

                await switchDefaultActivation(selectedActivationData.id, {
                    onSuccess() {
                        setIsConfirmModalOpen(false);

                        onSuccess();

                        showSuccessToast(
                            translate(CONFIRM_DEFAULT_ACTIVATION_TOAST_MESSAGE, {
                                name: selectedActivationData.name,
                            }),
                        );
                        track.trackDefaultHomepageUpdate();

                        void refetch();

                        if (withRedirect) {
                            redirectToDefaultPage(selectedActivationData.id, selectedActivationData.country);
                        }
                    },
                });
            } catch (error) {
                showSuccessToast(translate(COMMON_API_ERROR_MESSAGE));

                Sentry.error(`Error setting default homepage -`, error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const removeDefaultActivation = async (name: string, onSuccess = noop) => {
        if (defaultActivationId) {
            setIsLoading(true);

            await switchDefaultActivation(defaultActivationId, {
                onSuccess() {
                    showSuccessToast(translate(CONFIRM_DEFAULT_ACTIVATION_TOAST_MESSAGE_REMOVE, { name }));
                    track.trackDefaultHomepageRemove();
                    onSuccess();
                    void refetch();
                },
                onSettled() {
                    setIsLoading(false);
                },
            });
        }
    };

    const toggleDefaultActivation = async (activationId: string, onSuccess = noop, withRedirect = true) => {
        const isDefault = defaultActivationId === activationId;
        const currentActivation = activationsList?.find((item) => item.id === activationId);
        const name = currentActivation?.name;
        const country = currentActivation?.country;

        if (isDefault) {
            await removeDefaultActivation(name, onSuccess);

            return isConfirmModalOpen;
        }

        await setDefaultPage({ activationId, name, country, withRedirect, onSuccess });
    };

    return {
        setDefaultPage,
        setIsConfirmModalOpen,
        updateDefaultActivation,
        selectedActivationData,
        isConfirmModalOpen,
        defaultActivationId,
        isLoading,
        toggleDefaultActivation,
    };
};

export default useDefaultActivation;
